/** @format */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView as appInsightsTrackPageView } from '@services/applicationInsights';
import { trackPageView as gaTrackPageView } from '@services/googleAnalytics';
import { isClarityAvailable } from '@services/clarity';

declare global {
  interface Window {
    clarity?: (command: string, ...args: any[]) => void;
  }
}

/**
 * Custom hook for automatic page view tracking
 * Dual tracking: Application Insights + Google Analytics + Microsoft Clarity
 * Usage: Call this hook in the main App component
 */
export const useClarityPageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    const pageName = location.pathname;
    const pageUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
    const fullUrl = window.location.href;
    const pageTitle = document.title;

    // Application Insights tracking
    appInsightsTrackPageView(pageName, pageUrl, {
      search: location.search,
      hash: location.hash,
      state: location.state
    });

    // Google Analytics tracking
    gaTrackPageView(pageName, pageTitle, {
      page_location: pageUrl,
      page_search: location.search,
      page_hash: location.hash
    });

    // Microsoft Clarity - manually trigger for SPA/hash routing
    if (isClarityAvailable() && window.clarity) {
      window.clarity('set', 'current_url', fullUrl);
      window.clarity('event', 'virtual_page_view');
      if (process.env.NODE_ENV === 'development') {
        console.log('Clarity page view:', pageName);
      }
    }
  }, [location]);
};
